<template>
  <vbt-dialog
    v-model="dialog"
    :title="`${create ? 'Create' : 'Update'} replacement`"
    :submit-btn-name="create ? 'Create' : 'Update'"
    @submit="submitReplacement"
    @close="resetForm"
  >
    <v-layout>
      <v-flex sm6>
        <vbt-date-picker
          v-model="form.dates"
          :max-date="getMaxAllowedDate()"
          label="From - To (UTC+0)"
          range
        />
      </v-flex>
    </v-layout>

    <v-layout
      class="mt-6 mb-2"
      column
    >
      <strong>
        Substrate
      </strong>

      <v-divider />

      <v-layout>
        <v-flex sm4>
          <v-select
            v-model="form.substrate.name"
            :loading="substratesLoading"
            :items="substrates"
            label="Name"
            clearable
          />
        </v-flex>

        <v-flex
          class="px-5"
          sm4
        >
          <v-text-field
            v-model.trim.number="form.substrate.width"
            :rules="[validateDimension]"
            label="Width"
            type="number"
            clearable
          />
        </v-flex>

        <v-flex sm4>
          <v-text-field
            v-model.trim.number="form.substrate.height"
            :rules="[validateDimension]"
            label="Height"
            type="number"
            clearable
          />
        </v-flex>
      </v-layout>
    </v-layout>

    <v-layout column>
      <strong>
        Replacement
      </strong>

      <v-divider />

      <v-layout>
        <v-flex sm4>
          <v-text-field
            v-model.trim="form.replacement.name"
            label="Name"
            clearable
          />
        </v-flex>

        <v-flex
          class="px-5"
          sm4
        >
          <v-text-field
            v-model.trim.number="form.replacement.width"
            :rules="[validateDimension]"
            label="Width"
            type="number"
            clearable
          />
        </v-flex>

        <v-flex sm4>
          <v-text-field
            v-model.trim.number="form.replacement.height"
            :rules="[validateDimension]"
            label="Height"
            type="number"
            clearable
          />
        </v-flex>
      </v-layout>
    </v-layout>
  </vbt-dialog>
</template>

<script>
import { mapState } from 'vuex';

import { wrapToLoadingFn } from '@helpers';

import { useDialog } from '@mixins/factories';

import { LOCATIONS_NAMESPACE } from '@store/modules/locations.types';

import { ReplacementsApiService } from '../_services/replacements.api.service';

import {
  createReplacementForm,
  fillReplacementFormFromData,
  prepareReplacementData,
} from '../_meta/replacement';

export default {
  name: 'PbReplacementEditor',

  props: {
    create: {
      type: Boolean,
      required: true,
    },
    replacement: {
      type: Object,
      default: () => ({}),
    },
  },

  mixins: [
    useDialog({ watchHandlerName: 'fillForm' }),
  ],

  data() {
    return {
      form: createReplacementForm(),

      substratesLoading: false,
      substrates: [],
    };
  },

  computed: {
    ...mapState(LOCATIONS_NAMESPACE, ['suffix']),
  },

  watch: {
    suffix: {
      handler(newValue) {
        if (newValue) {
          this.getSubstrates(newValue);
        }
      },
      immediate: true,
    },
  },

  methods: {
    submitReplacement() {
      const request = this.create
        ? ReplacementsApiService.createReplacement
        : ReplacementsApiService.updateReplacement;

      this.$VBlackerTheme.alert.warning({
        text: `${this.create ? 'Create' : 'Update'} replacement?`,
      }, () => this.wrapToLoadingFn({
        req: request.bind({}, {
          data: prepareReplacementData(this.form),
          id: this.replacement.id,
          suffix: this.suffix,
        }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success(`Successfully ${this.create ? 'created' : 'updated'}`);
          this.$emit('success');
          this.dialog = false;
        },
      }));
    },

    getSubstrates(suffix) {
      this.wrapToLoadingFn({
        req: ReplacementsApiService.getSubstrates.bind({}, { suffix }),
        loadingFlagName: 'substratesLoading',
        onSuccess: (r) => {
          this.substrates = r.map(({ name }) => name);
        },
      });
    },

    fillForm(isOpenDialog) {
      if (!isOpenDialog) {
        return;
      }

      this.$set(
        this,
        'form',
        this.create
          ? createReplacementForm()
          : fillReplacementFormFromData(this.replacement),
      );
    },

    resetForm() {
      this.$set(this, 'form', createReplacementForm());
    },

    getMaxAllowedDate() {
      const today = new Date();
      const nextYears = today.setDate(today.getFullYear() + 5);

      return (new Date(nextYears)).toISOString();
    },

    validateDimension(value) {
      return (value === null) || (value >= 0) || 'Should be greater or equal 0';
    },

    wrapToLoadingFn,
  },
};
</script>
